/* ============================================================================
   Product 360 View — location pricing (sticky bar)
   Reads window.variantLocationPrices, resolves the user's state/city, and keeps
   the sticky bar price + state dropdown in sync with the active variant.
   Listens to `p360:variantchange` fired by <product-360-view>.
   ========================================================================== */

(function () {
  const wrapper = document.querySelector('.p360-price-wrapper');
  if (!wrapper) return;

  const priceEl = wrapper.querySelector('.p360-dynamic-price');
  const locationEl = wrapper.querySelector('.p360-dynamic-location');
  const priceText = wrapper.querySelector('.p360-price-text');
  const stateDropdowns = wrapper.querySelectorAll('.p360-state-selector');
  const defaultPrice = wrapper.dataset.defaultPrice || '';

  const STORAGE_KEY = 'jawa_user_location';
  const cityAliases = {
    Bengaluru: 'Bangalore',
    'Bangalore Urban': 'Bangalore',
    Bombay: 'Mumbai',
    Madras: 'Chennai',
    Trivandrum: 'Thiruvananthapuram',
  };

  let cachedLocation = null;
  let currentVariantId =
    document.querySelector('.p360__swatch.active')?.id ||
    document.querySelector('.p360__swatch.active')?.dataset.variantId ||
    document.querySelector('.p360__swatch')?.id ||
    document.querySelector('.p360__swatch')?.dataset.variantId ||
    null;

  /* ------------------------------ helpers ------------------------------- */

  function setLoading(on) {
    wrapper.classList.toggle('loading', on);
    if (priceText) priceText.classList.toggle('loading', on);
  }

  function renderPrice(price) {
    if (priceEl) priceEl.textContent = price || '—';
    if (locationEl) locationEl.textContent = 'Ex-Showroom Price*';
  }

  function getUserLocation() {
    if (cachedLocation) return Promise.resolve(cachedLocation);

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        cachedLocation = JSON.parse(stored);
        return Promise.resolve(cachedLocation);
      } catch (e) {
        /* ignore malformed cache */
      }
    }

    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const { latitude: lat, longitude: lon } = pos.coords;
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
            );
            const data = await res.json();

            let city =
              data.city ||
              data.locality ||
              data.localityInfo?.administrative?.[2]?.name ||
              '';
            const state = data.principalSubdivision || '';
            city = cityAliases[city] || city;

            cachedLocation = { city, state };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cachedLocation));
            resolve(cachedLocation);
          } catch (e) {
            resolve(null);
          }
        },
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }

  function populateStates(variantData, selectedState) {
    if (!stateDropdowns.length) return;
    stateDropdowns.forEach((dropdown) => {
      dropdown.innerHTML = '';
      Object.keys(variantData.states || {})
        .sort()
        .forEach((state) => {
          const opt = document.createElement('option');
          opt.value = state;
          opt.textContent = state;
          if (state === selectedState) opt.selected = true;
          dropdown.appendChild(opt);
        });
    });
  }

  /* ------------------------------ core ---------------------------------- */

  async function updateForVariant(variantId) {
    const variantData = window.variantLocationPrices?.[variantId];

    // No pricing map for this variant -> fall back to the default price.
    if (!variantData || typeof variantData !== 'object') {
      renderPrice(defaultPrice);
      setLoading(false);
      return;
    }

    setLoading(true);
    if (priceEl) priceEl.textContent = 'Loading…';

    let finalPrice = variantData.default?.price || defaultPrice;
    let selectedState = variantData.default?.location || 'Delhi';

    const location = await getUserLocation();
    if (location) {
      const stateData = variantData.states?.[location.state];
      const cityData = stateData?.[location.city];

      if (cityData?.price) {
        finalPrice = cityData.price;
        selectedState = location.state;
      } else if (stateData?.price) {
        finalPrice = stateData.price;
        selectedState = location.state;
      }
    }

    // Make sure the selected state actually exists in the data set.
    const availableStates = Object.keys(variantData.states || {});
    if (!availableStates.includes(selectedState)) {
      selectedState = availableStates.includes('Delhi')
        ? 'Delhi'
        : availableStates[0];
      const fallback = variantData.states?.[selectedState];
      if (fallback?.price) finalPrice = fallback.price;
    }

    populateStates(variantData, selectedState);
    renderPrice(finalPrice);
    setLoading(false);
  }

  /* ------------------------------ init ---------------------------------- */

  setLoading(true);
  if (priceEl) priceEl.textContent = 'Loading…';

  setTimeout(() => {
    if (currentVariantId) updateForVariant(currentVariantId);
    else {
      renderPrice(defaultPrice);
      setLoading(false);
    }
  }, 300);

  /* ----------------------------- events --------------------------------- */

  // Variant change dispatched by <product-360-view>.
  document.addEventListener('p360:variantchange', (e) => {
    const id = e.detail?.variantId;
    if (!id || id === currentVariantId) return;
    currentVariantId = id;
    updateForVariant(id);
  });

  // Manual state change from the dropdown (synced across desktop & mobile).
  stateDropdowns.forEach((dropdown) => {
    dropdown.addEventListener('change', (e) => {
      const selectedVal = e.target.value;
      stateDropdowns.forEach((d) => {
        d.value = selectedVal;
      });
      const variantData = window.variantLocationPrices?.[currentVariantId];
      if (!variantData) return;
      const stateData = variantData.states?.[selectedVal];
      renderPrice(stateData?.price || variantData.default?.price || defaultPrice);
    });
  });
})();
